/**
 * `AIContextEngineModel.Core.ts` — port of Unity's
 * `AIContextEngineModel.Core.cs` (interface + cache + interaction state).
 * Digest builders live in `AIContextEngineModel.SceneProjectUserContext.ts`,
 * mirroring the Unity partial split.
 *
 * Web slice deferral: the hierarchy helper agent (`HierarchyTransport`) is
 * not ported — `querySceneContext` runs local string matching only. The
 * knowledge/docs agent (`KnowledgeTransport`) IS ported; see
 * `AIContextEngineModel.KnowledgeTransport.ts`.
 */

import { Emitter } from "@core/events/emitter";
import { Ioc } from "@core/ioc/ioc";
import { animationDecodeGeneration } from "@runtime/assets/animationClips";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import type { IProjectDocsModel } from "@models/docs/IProjectDocsModel";
import { PROJECT_DOCS_MODEL } from "@models/docs/IProjectDocsModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import { HOVER_MODEL, type IHoverModel } from "@models/scene/IHoverModel";
import { normalizeProductNameKey } from "@models/data/productDocumentDto";
import type { IProjectWorkflowModel } from "@models/workflow/IProjectWorkflowModel";
import { PROJECT_WORKFLOW_MODEL } from "@models/workflow/IProjectWorkflowModel";
import type { ICustomerVectorStoreModel } from "@ai/memory/ICustomerVectorStoreModel";
import { CUSTOMER_VECTOR_STORE_MODEL } from "@ai/memory/ICustomerVectorStoreModel";
import {
  aliasSegmentOf,
  buildPathIndex,
  displayNameOf,
  isMoved,
  normalizePath,
} from "./hierarchyPaths";
import {
  buildProjectContextText,
  buildSceneContextText,
  buildUserContextText,
  hasUserInteractionContext,
  type UserInteractionSnapshot,
  type UserInteractionState,
} from "./AIContextEngineModel.SceneProjectUserContext";
import {
  buildDocsInstruction,
  buildDocsPrompt,
  findPdfAsync,
  type KnowledgeContextResponse,
  type KnowledgeDeps,
  queryKnowledgeAsync,
  searchDocsByFileNameAsync,
} from "./AIContextEngineModel.KnowledgeTransport";

export interface IAIContextEngineModel {
  readonly onContextInvalidated: Emitter<void>;

  buildSceneContext(): string;
  buildProjectContext(): string;
  buildUserContext(): string;

  /** Immutable snapshot for durable question-time transcript context. */
  captureUserInteractionState(): UserInteractionSnapshot;
  /** Web snapshot-id equivalent of Unity's `SetCurrentlyPointedAtObject`. */
  setPointedSnapshotId(snapshotId: string | null): void;
  hasUserInteractionContext(): boolean;
  tryBuildUserContextForMessage(): string | null;
  tryBuildProjectContextForMessage(): string | null;

  /** Local hierarchy lookup backing `Get_Scene_Context`. */
  querySceneContext(prompt?: string | null, searchQuery?: ReadonlyArray<string> | null): string;
  /**
   * Local hierarchy/alias context plus the enriched document prompt for one
   * part. The executor hands that prompt to its companion
   * `Prompt_Search_Docs`, which is the ONE owner of the vector search.
   */
  queryPartContextAsync(prompt: string, partPath: string): Promise<PartContextQueryResult>;

  // -- Knowledge (documents) — Unity `AIContextEngineModel.Types.cs:17-27` --

  /** Appends the docs instruction to a prompt unless it already mentions docs. */
  buildDocsPrompt(prompt: string): string;
  /** Semantic document search over the customer vector store. */
  queryKnowledgeContextAsync(
    prompt: string,
    partContext: string,
    semanticOnly?: boolean,
    signal?: AbortSignal,
  ): Promise<KnowledgeContextResponse>;
  /** Filename-only document search over the library manifest (no model call). */
  searchDocsByFileNameAsync(prompt: string): Promise<KnowledgeContextResponse>;
  /** Resolve one PDF to open from a filename or short query. */
  findPdfAsync(prompt: string, signal?: AbortSignal): Promise<KnowledgeContextResponse>;

  invalidateCache(): void;
  dispose(): void;
}

/**
 * How many distinct knowledge queries a session remembers.
 *
 * Generous on purpose — an entry is one prose response, and the cost of a miss
 * is a `gpt-5.5` round trip measured at 5.7 s. The bound exists to stop a long
 * session growing without limit, not to ration hits.
 */
const K_KNOWLEDGE_CACHE_MAX = 64;

/** Port of Unity's `PartContextQueryResult` (`AIContextEngineModel.Types.cs:54`). */
export interface PartContextQueryResult {
  readonly text: string;
  readonly docsPrompt: string | null;
}

export class AIContextEngineModel implements IAIContextEngineModel {
  readonly onContextInvalidated = new Emitter<void>();

  private readonly m_project: IProjectModel;
  private readonly m_isWriter: () => boolean;
  private readonly m_offFns: Array<() => void> = [];

  private m_interaction: UserInteractionState = {
    selectedSnapshotIds: [],
    lastSelectionAt: 0,
    lastSelectedSnapshotIds: [],
    pointedSnapshotId: null,
    lastPointedAt: 0,
    lastPointedSnapshotId: null,
  };

  private m_cachedSceneContext: string | null = null;
  private m_cachedSceneKey: string | null = null;

  /** See `queryKnowledgeContextAsync`. Keyed by corpus stamp, so self-invalidating. */
  private readonly m_knowledgeCache = new Map<string, KnowledgeContextResponse>();

  constructor(project: IProjectModel, selection: ISelectionModel, isWriter: () => boolean) {
    this.m_project = project;
    this.m_isWriter = isWriter;

    this.m_offFns.push(
      selection.onChanged.on((change) => {
        const previous = this.m_interaction.selectedSnapshotIds;
        if (previous.length > 0) {
          this.m_interaction = {
            ...this.m_interaction,
            lastSelectedSnapshotIds: previous,
            lastSelectionAt: Date.now(),
          };
        }
        this.m_interaction = {
          ...this.m_interaction,
          selectedSnapshotIds: [...change.selectedIds],
        };
      }),
      project.onEntityChangesApplied.on(() => this.invalidateCache()),
      project.onActiveSceneChanged.on(() => this.invalidateCache()),
      project.onScenesListChanged.on(() => this.invalidateCache()),
      project.onMediaTilesChanged.on(() => this.invalidateCache()),
      project.onSelectionSetsChanged.on(() => this.invalidateCache()),
    );

    // Pointing — the deictic half of User Context ("what is this?", "how many
    // of these"). Taken from IOC rather than the constructor because the hover
    // model is a viewport concern the headless context engine can run without;
    // when no viewport registered one, pointing simply never reports.
    if (Ioc.isRegistered<IHoverModel>(HOVER_MODEL)) {
      const hover = Ioc.resolve<IHoverModel>(HOVER_MODEL);
      this.m_offFns.push(
        hover.onChanged.on(({ snapshotId }) => {
          this.setPointedSnapshotId(this.resolvePointableSnapshotId(snapshotId));
        }),
      );
    }
  }

  dispose(): void {
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
  }

  buildSceneContext(): string {
    // ⚠ The decode generation is part of the key. The digest reports each
    // model's animation takes, which are only knowable once its glTF has
    // decoded — and placing a model into an ALREADY-loaded scene opens no
    // scene-load session, so the subscription below never fires for it. Without
    // this term the line stays takes-less for the rest of the scene's life.
    const key = `${this.m_project.activeProjectId}|${this.m_project.activeSceneId}`
      + `|${animationDecodeGeneration()}`;
    if (this.m_cachedSceneContext !== null && this.m_cachedSceneKey === key) {
      return this.m_cachedSceneContext;
    }
    const context = buildSceneContextText(this.m_project);
    this.m_cachedSceneContext = context;
    this.m_cachedSceneKey = key;
    return context;
  }

  buildProjectContext(): string {
    return buildProjectContextText(this.m_project);
  }

  buildUserContext(): string {
    return buildUserContextText(this.m_project, this.m_interaction, this.m_isWriter());
  }

  captureUserInteractionState(): UserInteractionSnapshot {
    const referencedIds = new Set<string>([
      ...this.m_interaction.selectedSnapshotIds,
      ...this.m_interaction.lastSelectedSnapshotIds,
    ]);
    if (this.m_interaction.pointedSnapshotId) {
      referencedIds.add(this.m_interaction.pointedSnapshotId);
    }
    if (this.m_interaction.lastPointedSnapshotId) {
      referencedIds.add(this.m_interaction.lastPointedSnapshotId);
    }

    const snapshotNames: Record<string, string> = {};
    const sceneId = this.m_project.activeSceneId;
    if (sceneId && referencedIds.size > 0) {
      for (const entity of this.m_project.getEntities(sceneId)) {
        for (const snapshot of entity.snapshots) {
          if (!referencedIds.has(snapshot.id)) continue;
          const name = displayNameOf(snapshot).trim();
          if (name) snapshotNames[snapshot.id] = name;
        }
      }
    }

    return Object.freeze({
      selectedSnapshotIds: Object.freeze([...this.m_interaction.selectedSnapshotIds]),
      lastSelectionAt: this.m_interaction.lastSelectionAt,
      lastSelectedSnapshotIds: Object.freeze([...this.m_interaction.lastSelectedSnapshotIds]),
      pointedSnapshotId: this.m_interaction.pointedSnapshotId,
      lastPointedAt: this.m_interaction.lastPointedAt,
      lastPointedSnapshotId: this.m_interaction.lastPointedSnapshotId,
      snapshotNames: Object.freeze(snapshotNames),
    });
  }

  /**
   * Hover reports whatever is under the cursor; POINTING is about scene
   * ENTITIES, and the two are not the same set.
   *
   * Media tiles are tagged pickable (`mediaTileRenderer.ts` — `tagSnapshot`)
   * and the viewport's hover writer filters only annotations, so a text card's
   * id genuinely arrives here. That is worse than a line that fails to
   * describe, because {@link setPointedSnapshotId} promotes whatever it
   * DISPLACES into `lastPointedSnapshotId`: an unvalidated id launders itself
   * into the referent memory on the very next move, `describe()` then answers
   * null for it, and the `else if` chain it has already entered suppresses the
   * `Last Selected Part` fallback too — so the turn reaches the agent with no
   * referent line at all. "I hovered that part and asked what it was" then
   * fails BECAUSE the cursor crossed a card on its way to the panel.
   *
   * Filtered HERE rather than at the writers, deliberately: this is the one
   * place that serves all three of them — the desktop pointer, the XR ray, and
   * `HierarchyView`'s ungated row hover — and it leaves the OUTLINE free to
   * highlight a card, which is what the viewport has always done. It also makes
   * this subscription agree with `ViewportView.resolveSparkPointerSnapshotId`,
   * which already applies exactly this check on the parallel Spark path; the
   * two writers disagreeing is what made the laundering reachable.
   */
  private resolvePointableSnapshotId(snapshotId: string | null): string | null {
    const sceneId = this.m_project.activeSceneId;
    if (!snapshotId || !sceneId) return null;
    return this.m_project.getEntityBySnapshotId(sceneId, snapshotId) === null
      ? null
      : snapshotId;
  }

  setPointedSnapshotId(snapshotId: string | null): void {
    const previous = this.m_interaction.pointedSnapshotId;
    if (previous === snapshotId) return;
    if (previous) {
      this.m_interaction = {
        ...this.m_interaction,
        lastPointedSnapshotId: previous,
        lastPointedAt: Date.now(),
      };
    }
    this.m_interaction = { ...this.m_interaction, pointedSnapshotId: snapshotId };
  }

  hasUserInteractionContext(): boolean {
    return hasUserInteractionContext(this.m_interaction);
  }

  tryBuildUserContextForMessage(): string | null {
    if (!this.hasUserInteractionContext()) return null;
    return this.buildUserContext();
  }

  tryBuildProjectContextForMessage(): string | null {
    const context = this.buildProjectContext();
    return context && context !== `{"project":null}` ? context : null;
  }

  querySceneContext(
    prompt?: string | null,
    searchQuery?: ReadonlyArray<string> | null,
  ): string {
    const full = this.buildSceneContext();
    const explicitTerms = (searchQuery ?? [])
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const promptTerms = collectSearchTerms(prompt, null);

    const filterBy = (terms: string[]): string[] =>
      full.split("\n").filter((line) => {
        const lower = line.toLowerCase();
        return terms.some((t) => lower.includes(t));
      });

    if (explicitTerms.length > 0) {
      const matches = filterBy(explicitTerms);
      if (matches.length > 0) return matches.join("\n");
      // Explicit names that miss fall through to prompt terms, then full.
    }
    if (promptTerms.length > 0) {
      const matches = filterBy(promptTerms);
      if (matches.length > 0) return matches.join("\n");
    }
    if (explicitTerms.length > 0 && promptTerms.length === 0) {
      return `No matches found for: ${explicitTerms.join(", ")}. Call again without SearchQuery to get the full scene context.`;
    }
    // No usable terms (or prompt-style question): return the full digest —
    // Unity would consult the hierarchy helper agent here (deferred).
    return full;
  }

  /**
   * Unity `QueryPartContextAsync` (`Core.cs:153-211`): the part's own line
   * plus its ancestor chain becomes the "Part Context" payload, and the user's
   * question plus a bottom-up name/notes priority list becomes the docs prompt.
   *
   * Deliberate web ownership split: this method performs NO document search.
   * `ContextExecutor` gives `docsPrompt` to its registered
   * `Prompt_Search_Docs` companion, so a part question produces one vector
   * request rather than one part-scoped request plus one broad request.
   */
  async queryPartContextAsync(
    prompt: string,
    partPath: string,
  ): Promise<PartContextQueryResult> {
    if (!prompt?.trim()) return { text: "error: prompt_required", docsPrompt: null };
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return { text: "error: no active scene", docsPrompt: null };

    const normalized = normalizePath(partPath);
    const index = buildPathIndex(this.m_project.getEntities(sceneId));
    const entry = index.find((e) => e.path === normalized);
    if (!entry) {
      return { text: `error: part not found at path '${partPath}'`, docsPrompt: null };
    }

    // The hierarchy chain, target first then ancestors — the same order Unity
    // builds it in, and the order the docs priority list reverses.
    const chain = [entry];
    let path = entry.path;
    while (path.includes(",")) {
      path = path.slice(0, path.lastIndexOf(","));
      const ancestor = index.find((e) => e.path === path);
      if (ancestor) chain.push(ancestor);
    }

    const targetLine =
      `${entry.path}: "${displayNameOf(entry.snapshot)}"`
      + `${aliasSegmentOf(entry.snapshot)}${isMoved(entry.snapshot) ? " (moved)" : ""}`;
    const partContext = [
      targetLine,
      ...chain
        .slice(1)
        .map((a) => `parent ${a.path}: "${displayNameOf(a.snapshot)}"`),
    ].join("\n");

    const trimmedPrompt = prompt.trim();
    const promptWithPath = trimmedPrompt.includes(entry.path)
      ? trimmedPrompt
      : `${trimmedPrompt} (part path ${entry.path})`;
    let docsPrompt = `${promptWithPath} | Part details: ${targetLine}`;
    docsPrompt += buildDocsInstruction(docsPrompt);

    // Unity `BuildDocsSearchPriorityInstruction`: bottom-up from the selected
    // part to its parents, so the retrieval favours the most specific name.
    const terms: string[] = [];
    const seen = new Set<string>();
    for (let i = chain.length - 1; i >= 0; i--) {
      const name = displayNameOf(chain[i].snapshot)?.trim();
      if (name && !seen.has(name.toLowerCase())) {
        seen.add(name.toLowerCase());
        terms.push(name);
      }
    }
    if (terms.length > 0 && !docsPrompt.toLowerCase().includes("docs search priority")) {
      docsPrompt +=
        ` | Docs search priority (bottom-up from selected part to parents; use name then notes/annotations): ${terms.join("; ")}`;
    }

    return {
      text: [`Target: ${targetLine}`, partContext].join("\n\n"),
      docsPrompt,
    };
  }

  // ---- Knowledge (documents) ----------------------------------------

  buildDocsPrompt(prompt: string): string {
    return buildDocsPrompt(prompt);
  }

  /**
   * The knowledge sub-agent, with an identical call answered from memory.
   *
   * ⚠ THIS EXISTS SO ATTRIBUTION CAN BE A SIDE EFFECT OF THE TOOL RATHER THAN A
   * GUESS. A repeated document question used to be answered by the agent from
   * its own transcript with no tool call, which left the answer with no chips —
   * so `useSpark` grew a memory that REPLAYED an earlier answer's sources
   * whenever the two answers looked alike. "Looked alike" was a text heuristic,
   * and it misfired exactly as a text heuristic does: a part named `Fan Blade 1`
   * tokenises to the bare figure `1`, any `1` in a retrieved passage matches it,
   * and the identity answer "That's Fan Blade 1." inherited the manual's chip
   * (user-reported 2026-09-16). A words-based third arm had already been tried
   * and reverted for the same class of reason (2026-08-29, language-unsafe).
   *
   * Two arms and two reverts is enough evidence: the app cannot reconstruct
   * "did this answer come from documents" by comparing prose. The only reliable
   * signal is whether a document tool RAN. So the prompt now tells the agent to
   * call again rather than reuse (`answer.md` → Facts and Specs), and this cache
   * makes that free — the second call returns the first call's response, chips
   * and all, without a `gpt-5.5` round trip. Attribution is correct by
   * construction rather than by resemblance, and `useSpark` needs no memory.
   *
   * Keyed the same way `m_cachedSceneContext` is: a key that CHANGES when the
   * underlying data changes, rather than a subscription that has to be torn
   * down. The corpus stamp is `docs.items`, which is synchronous, so adding,
   * removing or re-uploading a document misses the cache without any
   * invalidation call.
   *
   * Never caches a failure — every error path in `queryKnowledgeAsync` returns
   * text prefixed `error:`, and caching one would pin a transient outage for the
   * life of the session. A genuine "nothing found" IS cached: same question,
   * same corpus, same answer, and it is the most expensive miss to repeat.
   */
  queryKnowledgeContextAsync(
    prompt: string,
    partContext: string,
    semanticOnly = false,
    signal?: AbortSignal,
  ): Promise<KnowledgeContextResponse> {
    const key = this.knowledgeCacheKey(prompt, partContext, semanticOnly);
    const hit = key === null ? undefined : this.m_knowledgeCache.get(key);
    if (hit) {
      // `usage` is dropped on a hit: no tokens were spent, and the executors
      // feed it straight to `trackHelperAgentUsage`, which would otherwise bill
      // the same round trip once per repeat.
      return Promise.resolve({ ...hit, usage: null });
    }
    return queryKnowledgeAsync(
      this.knowledgeDeps(),
      partContext,
      prompt,
      semanticOnly,
      signal,
    ).then((response) => {
      if (key !== null && !response.text.trimStart().startsWith("error:")) {
        this.m_knowledgeCache.set(key, response);
        while (this.m_knowledgeCache.size > K_KNOWLEDGE_CACHE_MAX) {
          const oldest = this.m_knowledgeCache.keys().next();
          if (oldest.done) break;
          this.m_knowledgeCache.delete(oldest.value);
        }
      }
      return response;
    });
  }

  /**
   * `null` when the docs model is not resolvable yet — an unkeyable call is
   * simply not cached rather than cached under a key that cannot be invalidated.
   */
  private knowledgeCacheKey(
    prompt: string,
    partContext: string,
    semanticOnly: boolean,
  ): string | null {
    const docs = this.knowledgeDeps().docs();
    if (!docs) return null;
    const corpus = docs.items
      .map((item) => `${item.storagePath}@${item.lastModifiedMs}`)
      .join("|");
    // JSON rather than a delimiter: a prompt is free text, so any separator
    // character could appear inside one and let two different queries collide
    // on one key — which would serve the wrong answer AND the wrong chips.
    return JSON.stringify([corpus, semanticOnly, partContext, prompt]);
  }

  searchDocsByFileNameAsync(prompt: string): Promise<KnowledgeContextResponse> {
    return searchDocsByFileNameAsync(this.knowledgeDeps(), prompt);
  }

  findPdfAsync(prompt: string, signal?: AbortSignal): Promise<KnowledgeContextResponse> {
    return findPdfAsync(this.knowledgeDeps(), prompt, signal);
  }

  /**
   * Resolved lazily and defensively: the vector store is registered only when
   * `VITE_AI_ENABLED` is on, document association comes from the loaded
   * workflow's `product`, and a cross-account project's documents live in the
   * OWNING account's partition (same precedence as `ProjectModel.getStorageContext`).
   */
  private knowledgeDeps(): KnowledgeDeps {
    return {
      docs: () =>
        Ioc.isRegistered<IProjectDocsModel>(PROJECT_DOCS_MODEL)
          ? Ioc.resolve<IProjectDocsModel>(PROJECT_DOCS_MODEL)
          : null,
      vectorStore: () =>
        Ioc.isRegistered<ICustomerVectorStoreModel>(CUSTOMER_VECTOR_STORE_MODEL)
          ? Ioc.resolve<ICustomerVectorStoreModel>(CUSTOMER_VECTOR_STORE_MODEL)
          : null,
      customerId: () => {
        const fromProject = this.m_project.activeProject?.customerId;
        if (fromProject) return fromProject;
        if (!Ioc.isRegistered<IAuthModel>(AUTH_MODEL)) return null;
        return Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser?.customerId ?? null;
      },
      productKey: () => {
        if (!Ioc.isRegistered<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL)) return null;
        const key = normalizeProductNameKey(
          Ioc.resolve<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL).loadedWorkflow?.product,
        );
        return key || null;
      },
      workflowId: () =>
        Ioc.isRegistered<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL)
          ? Ioc.resolve<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL).loadedWorkflow?.uid?.trim()
            || null
          : null,
    };
  }

  invalidateCache(): void {
    this.m_cachedSceneContext = null;
    this.m_cachedSceneKey = null;
    // Belt and braces: the knowledge cache is already keyed by the corpus, so a
    // document change misses it without this. Clearing here covers a project or
    // scene change, where the corpus stamp can be momentarily unchanged while
    // the question means something else entirely.
    this.m_knowledgeCache.clear();
    this.onContextInvalidated.emit();
  }
}

const PROMPT_STOP_WORDS = new Set([
  "return", "objects", "object", "identified", "the", "and", "for", "with",
  "current", "scene", "top", "level", "root", "models", "model", "parts",
  "part", "all", "list", "show", "that", "this", "are", "any",
]);

function collectSearchTerms(prompt?: string | null, _unused?: null): string[] {
  // Unity's local pass tries meaningful words from the prompt before falling
  // back to the hierarchy agent; keep words of length >= 3 minus boilerplate.
  const terms: string[] = [];
  if (prompt) {
    for (const word of prompt.toLowerCase().split(/[^a-z0-9_]+/)) {
      if (word.length >= 3 && !PROMPT_STOP_WORDS.has(word)) terms.push(word);
    }
  }
  return terms;
}

export const AI_CONTEXT_ENGINE_MODEL = Symbol.for("Campfire.AIContextEngineModel");
